<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="530"
    @click:outside="onOkClickButton"
    @keydown.esc="onOkClickButton"
  >
    <div class="assets-score-popup">
      <span class='close_btn' @click='onOkClickButton'>
        <span class="material-icons-outlined">
          close
        </span>
      </span>
      <div class="title">{{$t(title)}}</div>
      <div class="message">
        <div class='assets-count-details-sections'>
          <div class="sub-title">{{ $t('asset.count.details.popup.top.level.domains.title') }}</div>
          <span class='sub-title__content__domains' :class='{closed: !isShowAll}' > {{ domains.join(', ') }} </span>
        </div>
        <div v-if='isDomainsListLong' class='hide-domains-btn' @click.stop="isShowAll = !isShowAll">
          <span>{{ captionOfShowAllToggleButton }}</span>
        </div>
        <div v-else class='hide-domains-btn'/>
        <div class='assets-count-details-data'>
          <div class='assets-count-details-sections' v-if='isMSSPFeatureToggleOn'>
            <div class="sub-title">{{ $t('asset.count.details.popup.tier.level.title') }}</div>
            <div class='sub-title__tier' :class='[tier]'> {{ currentTierSizeFormatted }} </div>
            <div class='label'> {{ currentTierSizeLegendFormatted }}</div>
          </div>
          <v-divider class='assets-count-details-data__divider' v-if='isMSSPFeatureToggleOn'
                     inset
                     vertical
          />
          <div class='assets-count-details-sections'>
            <div class="sub-title">{{ $t('asset.count.details.popup.unique.subdomains.title') }}</div>
            <span> {{ uniqueSubdomains }} </span>
          </div>
          <v-divider class='assets-count-details-data__divider'
                     inset
                     vertical
          />
          <div class='assets-count-details-sections'>
            <div class="sub-title">{{ $t('asset.count.details.popup.unique.ips.title') }}</div>
            <span> {{ uniqueIPs }} </span>
          </div>
        </div>
        <div class='note' v-if='isMissingData'>{{domainsWithMissingStatsFormatted}}</div>
      <div class="buttons">
        <v-btn small depressed class="yes-button btn-primary" @click="onOkClickButton()">
          {{$t(okCaption)}}
        </v-btn>
      </div>
      </div>
    </div>
  </v-dialog>

</template>

<script>

import { mapGetters } from 'vuex';
import { tierColorToSize, tierSizeFormattedShortString, tierSizeFormattedString } from '@/constants/game';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AssetsCountDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    tier: {
      type: String,
      required: true,
    },
    points: String,
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    domains: {
      type: Array,
      required: true,
      default: () => [],
    },
    stats: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      isShowAll: false,
    };
  },
  computed: {
    ...mapGetters(['generalStatistics', 'isMsspLicense']),
    isDomainsListLong() {
      return this.domains.join(', ').length > 45;
    },
    isMSSPFeatureToggleOn() {
      return this.isMsspLicense;
    },
    title() {
      return 'asset.count.details.popup.title';
    },
    okCaption() {
      return 'confirm.ok';
    },
    uniqueIPs() {
      if (this.stats && Object.keys(this.stats).length > 0) {
        return (this.stats.hosts?.total || 0) === 0 ? 'N/A' : this.stats.hosts.total;
      }
      return this.calculateAssets.amountHosts === 0 ? 'N/A' : this.calculateAssets.amountHosts;
    },
    uniqueSubdomains() {
      if (this.stats && Object.keys(this.stats).length > 0) {
        return this.stats.subdomains.alive === 0 ? 'N/A' : this.stats.subdomains.alive;
      }
      return this.calculateAssets.amountSubdomains;
    },
    isMissingData() {
      if (this.stats && Object.keys(this.stats).length > 0) {
        return false;
      }
      return this.calculateAssets.missingData.length > 0;
    },
    domainsWithMissingStatsFormatted() {
      return `${this.$t('asset.count.details.missing.stats.part1')} ${this.calculateAssets.missingData.join(' ,')}. ${this.$t('asset.count.details.missing.stats.part2')}`;
    },
    tierFormatted() {
      return this.tier.charAt(0).toUpperCase() + this.tier.slice(1);
    },
    currentTierSizeFormatted() {
      return tierSizeFormattedShortString[this.currentTierSize];
    },
    currentTierSizeLegendFormatted() {
      return tierSizeFormattedString[this.currentTierSize];
    },

    currentTierSize() {
      return tierColorToSize[this.tier];
    },
    calculateAssets() {
      const discovery = Object.values(this.generalStatistics).map(statistics => statistics.discovery).filter(item => item);
      let amountSubdomains = 0;
      let amountHosts = 0;
      const missingData = [];
      discovery.forEach(stats => {
        const hosts = (stats.statistics?.data?.hosts?.total || 0);
        amountSubdomains += (stats.statistics?.data?.subdomains?.alive || 0);
        amountHosts += hosts;
        if (hosts === 0) {
          missingData.push(stats.statistics.tld);
        }
      });
      return {amountSubdomains, amountHosts, missingData};
    },
    captionOfShowAllToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
  },
  methods: {
    onOkClickButton() {
      this.$emit('clicked', true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.assets-score-popup {
  .note {
    font-family: $default-application-old-font-regular;
    font-size: $font-size-sm;
    color: $delta-high-text;
  }

  .assets-count-details-data {
    &__divider {
      margin-left: $space-4;
      margin-right: $space-4;
      max-height: rem(70);
    }

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-bottom: $space-5;
  }

  max-width: rem(530);
  padding: $space-8 $space-7;
  background-color: var(--bg-primary);

  .close_btn {
    float: right;
    margin-top: rem(-26);
    margin-right: rem(-20);

    &:hover {
      cursor: pointer;
      color: var(--brand-primary);
    }
  }

  .title {
    margin-top: rem(-2);
    margin-bottom: $space-7;
    color: var(--text-primary);
    font-family: $default-application-font-bold;
    text-transform: capitalize;
    font-size: $font-size-3xl;
    font-weight: bold;
    line-height: $font-size-xl;
    letter-spacing: 0;
  }

  .sub-title {
    &__tier {
      font-family: $default-application-font-bold;
      font-size: $font-size-5xl;
      margin-top: rem(-11);

      &.blue {
        background: $gradient-blue;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      &.bronze {
        background: $gradient-bronze;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      &.silver {
        background: $gradient-silver;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      &.gold {
        background: $gradient-gold;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      &.platinum {
        background: $gradient-platinum;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      &.purple {
        background: $gradient-purple;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    color: var(--text-secondary);
    font-family: $default-application-font-regular;
    letter-spacing: 1.1px;
    line-height: $space-4;
    text-transform: uppercase;
    font-size: $font-size-sm;
    padding-bottom: rem(5);

    &__content {
      &__domains {
        text-transform: uppercase;
        font-size: $font-size-base;
        font-family: $default-application-font-medium;
        line-height: $space-6;

        &.closed {
          padding-top: rem(5);
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-line-clamp: 1;
        }
      }
    }
  }

  .message {
    font-size: $font-size-base;
    line-height: 1.36;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;

    .hide-domains-btn {
      color: var(--brand-secondary);
      font-family: $default-application-font-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      padding-bottom: $space-4;
      text-align: right;
      width: 100%;

      &:hover {
        cursor: pointer;
      }
    }

    .assets-count-details-sections {
      font-family: $default-application-font-semi-bold;
      font-size: $font-size-3xl;

      .label {
        color: var(--text-secondary);
        font-family: $default-application-font-regular;
        font-size: $font-size-2xs;
        letter-spacing: 1.1px;
        line-height: $space-4;
        text-transform: uppercase;
        min-width: max-content;
        margin-top: rem(-5);
      }
    }
  }

  .buttons {
    text-align: right;
    padding-top: $space-4;

    .no-button {
      box-sizing: border-box;
      height: $btn-height-md;
      width: rem(86);
      border: $border-width-thin solid var(--brand-primary);
      color: var(--brand-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      box-shadow: $shadow-sm;
    }

    .yes-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary) !important;
      box-shadow: $shadow-sm;
      color: var(--text-inverse) !important;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
      margin-right: rem(-10);
      margin-bottom: rem(-14);
    }
  }
}
</style>
